import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Trophy, ArrowUp, ArrowDown, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TopPicks({ timeframe, sensitivity }) {
  const { t, fmt, pn } = useI18n();
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/top-picks`, { params: { timeframe, sensitivity, limit: 5 } })
      .then(({ data }) => { if (mounted) setPicks(data.picks || []); })
      .catch(() => { if (mounted) setPicks([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [timeframe, sensitivity]);

  if (!loading && picks.length === 0) return null;

  return (
    <section className="border-b border-zinc-900" data-testid="top-picks">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Trophy className="h-3.5 w-3.5 text-amber-400" />
              {t("top_picks")}
            </h3>
            <p className="text-[11px] text-zinc-500">{t("top_picks_desc")}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {picks.map((p, i) => {
            const [from, to] = p.symbol.split("/");
            const isBuy = p.signal === "buy";
            return (
              <Link
                to={`/pair/${from}/${to}`}
                key={p.symbol}
                data-testid={`top-pick-${from}${to}`}
                className="block"
              >
                <Card className={cn(
                  "relative bg-[#121212] border rounded-sm p-4 transition-all hover:scale-[1.02]",
                  isBuy ? "border-emerald-500/40 hover:border-emerald-500/70" : "border-red-500/40 hover:border-red-500/70"
                )}>
                  <div className="absolute -top-2 -end-2 w-6 h-6 rounded-full bg-amber-500 text-black text-[10px] font-bold flex items-center justify-center num shadow-lg">
                    #{i + 1}
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="ticker text-sm font-bold text-white" dir="ltr">{p.symbol}</span>
                    <span className={cn("num text-[10px] font-bold px-1.5 py-0.5 rounded-sm",
                      isBuy ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
                    )}>{p.score}</span>
                  </div>
                  <div className="text-[10px] text-zinc-500 mb-3 truncate">{pn(p.symbol)}</div>
                  <div className={cn("flex items-center gap-1.5 text-base font-bold mb-2",
                    isBuy ? "text-emerald-400" : "text-red-400"
                  )}>
                    {isBuy ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                    <span>{isBuy ? t("buy") : t("sell")}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-zinc-500">{p.confidence}% · {fmt(p.expiry_seconds)}</span>
                    <span className={cn("num font-bold",
                      p.win_rate >= 60 ? "text-emerald-400" : p.win_rate >= 45 ? "text-zinc-300" : "text-red-400"
                    )}>{p.win_rate}%</span>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
